// @ts-nocheck
import { FoundationFieldDefinition } from "@store/Framework/Types/FoundationFieldDefinition";
import { getCssUnitFieldDefinition } from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import fieldDefinitionBuilder from "@store/Framework/Common/Utilities/FieldDefinitionBuilder/fieldDefinitionBuilder";
import { defaultCategoryCardCss } from "@store/Library/Widgets/Category/BunzlStoreCategoryList";
import { carouselDefaultCustomCss } from "@store/Overrides/Components/Carousel";
import translate from "@insite/client-framework/Translate";
import * as cssLinter from "css";
import { CategoryResponsiveFieldsBreakpoints } from "@store/Library/Widgets/Category/BunzlStoreCategoryList"; // Import this type

export const enum CategoryListSharedFieldKeys {
    customCSS = "customCSS",
    showArrows = "showArrows",
    carouselOverFlow = "carouselOverflow",
    numberOfItemsPerRow = "numberOfItemsPerRow",
    responsive = "responsive",
    gap = "gap",
    linkText = "linkText",
    showRangeOfCategories = "showRangeOfCategories",
    fromCategory = "fromCategory",
    toCategory = "toCategory",
    title = "title",
    titleType = "titleType",
    titleStyle = "titleStyle",
    content = "content",
    showDots = "showDots",
}

export interface CategoryListCommonFieldsProps {
    [CategoryListSharedFieldKeys.customCSS]: string;
    [CategoryListSharedFieldKeys.showArrows]: boolean;
    [CategoryListSharedFieldKeys.responsive]: CategoryResponsiveFieldsBreakpoints;
    [CategoryListSharedFieldKeys.carouselOverFlow]: boolean;
    [CategoryListSharedFieldKeys.gap]: string;
    [CategoryListSharedFieldKeys.linkText]: string;
    [CategoryListSharedFieldKeys.numberOfItemsPerRow]: number;
    useResponsiveOptions: boolean;
    [CategoryListSharedFieldKeys.showRangeOfCategories]: boolean;
    [CategoryListSharedFieldKeys.fromCategory]: number;
    [CategoryListSharedFieldKeys.toCategory]: number;
    [CategoryListSharedFieldKeys.title]: string;
    [CategoryListSharedFieldKeys.titleType]: string;
    [CategoryListSharedFieldKeys.titleStyle]: string;
    [CategoryListSharedFieldKeys.content]: string;
    [CategoryListSharedFieldKeys.showDots]: boolean;
}

export const defaultSharedCustomCss = `.category-list-title {
}
.category-list-content {
}
${defaultCategoryCardCss}${carouselDefaultCustomCss}
`;

const maxNumberOfItemsPerRowOptions = 8;
const advancedTab = {
    displayName: "Advanced",
    sortOrder: 100,
};

export function getSharedCategoryListFieldDefinitions(): FoundationFieldDefinition[] {
    const gapField = getCssUnitFieldDefinition(CategoryListSharedFieldKeys.gap);

    return [
        {
            name: CategoryListSharedFieldKeys.title,
            displayName: "Title",
            editorTemplate: "TextField",
            defaultValue: "",
            fieldType: "Contextual",
            sortOrder: 0,
        },
        {
            name: CategoryListSharedFieldKeys.titleType,
            displayName: "Title Type",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "H1", value: "h1" },
                { displayName: "H2", value: "h2" },
                { displayName: "H3", value: "h3" },
                { displayName: "H4", value: "h4" },
                { displayName: "H5", value: "h5" },
                { displayName: "H6", value: "h6" },
            ],
            defaultValue: "h1",
            fieldType: "Contextual",
            sortOrder: 1,
        },
        {
            name: CategoryListSharedFieldKeys.titleStyle,
            displayName: "Title Style",
            editorTemplate: "DropDownField",
            options: [
                { displayName: "Primary Header", value: "headerPrimary" },
                { displayName: "Secondary Header", value: "headerSecondary" },
                { displayName: "Tertiary Header", value: "headerTertiary" },
                { displayName: "H1", value: "h1" },
                { displayName: "H2", value: "h2" },
                { displayName: "H3", value: "h3" },
                { displayName: "H4", value: "h4" },
                { displayName: "H5", value: "h5" },
                { displayName: "H6", value: "h6" },
            ],
            defaultValue: "h2",
            fieldType: "General",
            sortOrder: 2,
        },
        {
            name: CategoryListSharedFieldKeys.content,
            displayName: "Content",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "Contextual",
            sortOrder: 3,
        },
        {
            name: CategoryListSharedFieldKeys.linkText,
            displayName: "LinkText",
            editorTemplate: "TextField",
            defaultValue: "Shop",
            fieldType: "Contextual",
            tooltip: translate("If left blank the category name will become the link text."),
            sortOrder: 10,
        },
        {
            ...gapField,
            defaultValue: "1.5rem",
            fieldType: "Contextual",
            sortOrder: 11,
        },
        ...fieldDefinitionBuilder([
            {
                name: CategoryListSharedFieldKeys.numberOfItemsPerRow,
                editorTemplate: "DropDownField",
                options: [...Array(maxNumberOfItemsPerRowOptions).keys()].map(index => ({
                    displayName: String(index + 1),
                    value: index + 1,
                })),
                defaultValue: 4,
                fieldType: "Contextual",
                sortOrder: 20,
            },
            {
                name: CategoryListSharedFieldKeys.showRangeOfCategories,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 21,
            },
            {
                name: CategoryListSharedFieldKeys.fromCategory,
                editorTemplate: "IntegerField",
                isVisible: item => item.fields.showRangeOfCategories,
                defaultValue: 0,
                fieldType: "Contextual",
                sortOrder: 22,
                validate: (value, item) => {
                    if (item.fields.showRangeOfCategories && item.fields.fromCategory > (value ?? 0)) {
                        return translate("From Category must be less than or equal to To Category");
                    }
                    return null;
                },
            },
            {
                name: CategoryListSharedFieldKeys.toCategory,
                editorTemplate: "IntegerField",
                isVisible: item => item.fields.showRangeOfCategories,
                defaultValue: 0,
                fieldType: "Contextual",
                sortOrder: 23,
                validate: (value, item) => {
                    if (item.fields.showRangeOfCategories && item.fields.fromCategory > (value ?? 0)) {
                        return translate("From Category must be less than or equal to To Category");
                    }
                    return null;
                },
            },
            {
                name: CategoryListSharedFieldKeys.carouselOverFlow,
                displayName: "Show Carousel",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 24,
            },
            {
                name: CategoryListSharedFieldKeys.showArrows,
                displayName: "Show Arrows",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 25,
                isVisible: item => item.fields.carouselOverflow,
            },
            {
                name: CategoryListSharedFieldKeys.showDots,
                displayName: "Show Dots",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 26,
                isVisible: item => item.fields.carouselOverflow,
            },
        ]),
        // Custom CSS (Advanced Tab)
        {
            name: CategoryListSharedFieldKeys.customCSS,
            displayName: "Custom CSS",
            editorTemplate: "CodeField",
            fieldType: "General",
            tab: advancedTab,
            defaultValue: defaultSharedCustomCss,
            isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            validate: value => {
                if (value === undefined || value === null) {
                    return "";
                }
                const result = cssLinter.parse(value, { silent: true });
                if (result?.stylesheet?.parsingErrors) {
                    return result.stylesheet.parsingErrors.length <= 0
                        ? ""
                        : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                }
                return "Unable to parse Css";
            },
            options: {
                mode: "css", // Use 'css' mode consistently
                lint: true,
                autoRefresh: true,
            },
        },
    ];
}
